import CDKIdEditor from '@/components/deal/cdk-id-editor';
import DeliveryDateSelector from '@/components/deal/delivery-date/delivery-date-selector';
import DealDeliveryPreferenceSelector from '@/components/deal/delivery-preference/deal-delivery-preference-selector';
import { DeliveryQueueSelector } from '@/components/deal/delivery-queue/delivery-queue-selector';
import Files from '@/components/deal/files/files';
import { FinanceManagerSelector } from '@/components/deal/finance-manager/finance-manager-selector';
import DealLenderSelector from '@/components/deal/lenders/deal-lender-selector';
import LenderDialog from '@/components/deal/lenders/lender-dialog.tsx';
import PickupDateSelector from '@/components/deal/pickup-date/pickup-date-selector';
import { SalesRepSelectors } from '@/components/deal/sales-reps/sales-reps-selectors';
import { DealStageSelector } from '@/components/deal/stage/deal-stage-selector';
import { DealStatusSelector } from '@/components/deal/status/deal-status-selector';
import TradeCreateButton from '@/components/deal/trade/trade-create-button';
import TradeInEditDialog from '@/components/deal/trade/trade-in-edit-dialog';
import { FloatingAIBulkSuggestions } from '@/components/ml/ai-bulk-suggestions';
import { getAppraisalPathname } from '@/components/nav/utils/url-builders';
import { Button } from '@/components/ui/button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { DealTimeStatusBadge } from '@/components/ui/deal-time-status-badge';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import {
  PDKBarcodeIcon,
  PDKCDKIcon,
  PDKDBBarcodeIcon,
  PDKOpenInNewTabIcon,
  PDKToastInfoBadgeIcon,
} from '@/components/ui/pdk-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import HotkeyDialogBoxes from '@/components/ux/all-list-and-board/hotkey-dialog-boxes';
import type { HotkeyDialogBox } from '@/components/ux/all-list-and-board/use-all-list-and-board-hotkeys';
import { getQueryParamsForScrollAndFocus } from '@/components/ux/hooks/use-scroll-and-focus';
import { useAISuggestionsContext } from '@/contexts/ai-suggestions-context';
import { useDealActions } from '@/hooks/use-deal-actions.tsx';
import useFavoriteMutation from '@/hooks/use-favorite-mutation';
import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags';
import { urlWithQueryParams } from '@/hooks/use-go-to-deal';
import useHubs from '@/hooks/use-hubs';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { cn } from '@/lib/utils';
import type { Deal } from '@/models/registry';
import { logbookStore } from '@/stores/logbook-store';
import { useNavigate } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import {
  BookText,
  ClockFading,
  Copy,
  FileQuestion,
  Link,
  ShieldAlert,
  ShieldCheck,
  Star,
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

const clipboardDealLink = () => {
  navigator.clipboard.writeText(window.location.href);
  toast({
    description: (
      <div className="flex flex-row gap-2 items-center">
        <PDKToastInfoBadgeIcon className="size-4" />
        <p>Deal link copied to clipboard</p>
      </div>
    ),
  });
};

const clipboardCDKId = (cdk_id: number | null) => {
  if (!cdk_id) {
    toast({
      description: (
        <div className="flex flex-row gap-2 items-center">
          <PDKToastInfoBadgeIcon className="size-4" />
          <p>No CDK ID found</p>
        </div>
      ),
    });
    return;
  }
  navigator.clipboard.writeText(cdk_id.toString());
  toast({
    description: (
      <div className="flex flex-row gap-2 items-center">
        <PDKToastInfoBadgeIcon className="size-4" />
        <p>CDK ID copied to clipboard</p>
      </div>
    ),
  });
};

const clipboardDealId = (dealId: string) => {
  navigator.clipboard.writeText(dealId);
  toast({
    description: (
      <div className="flex flex-row gap-2 items-center">
        <PDKToastInfoBadgeIcon className="size-4" />
        <p>Deal ID copied to clipboard</p>
      </div>
    ),
  });
};

const clipboardVIN = (vin: string | null | undefined) => {
  if (!vin) {
    toast({
      description: (
        <div className="flex flex-row gap-2 items-center">
          <PDKToastInfoBadgeIcon className="size-4" />
          <p>No VIN found</p>
        </div>
      ),
    });
    return;
  }

  navigator.clipboard.writeText(vin);
  toast({
    description: (
      <div className="flex flex-row gap-2 items-center">
        <PDKToastInfoBadgeIcon className="size-4" />
        <p>VIN copied to clipboard</p>
      </div>
    ),
  });
};

const LEFT_PADDING = 'px-2';

const NonMobileAside = observer(({ deal }: { deal: Deal }) => {
  const dealShortId = deal.short_id ?? '';
  useHotkeys(HOTKEYS.DEAL_CLIPBOARD_LINK, (e) => {
    e.preventDefault();
    e.stopPropagation();
    clipboardDealLink();
  });

  useHotkeys(HOTKEYS.DEAL_CLIPBOARD_CDK_ID, (e) => {
    e.preventDefault();
    e.stopPropagation();
    clipboardCDKId(deal.cdk_id);
  });

  useHotkeys(HOTKEYS.DEAL_CLIPBOARD_VIN, (e) => {
    e.preventDefault();
    e.stopPropagation();
    clipboardVIN(deal.vehicle_listing?.vehicle?.vin);
  });

  const { allSuggestions, allTaskSuggestions } = useAISuggestionsContext();

  const suggestionsForDeal = useMemo(() => {
    return allSuggestions.filter(
      (suggestion) => suggestion.deal_id === deal.id,
    );
  }, [allSuggestions, deal.id]);

  const taskSuggestionsForDeal = useMemo(() => {
    return allTaskSuggestions.filter(
      (suggestion) => suggestion.deal_id === deal.id,
    );
  }, [allTaskSuggestions, deal.id]);

  const navigate = useNavigate();
  const { hubPathIdentifier } = useHubs();

  const logbookRecord = logbookStore.getLogbookRecordsByCdkId(
    deal.cdk_id as number,
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isFeatureEnabled: isAISuggestionsEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.AI_SUGGESTIONS,
  });

  return (
    <div className="flex flex-col dark:bg-dark">
      <header className="flex flex-row border-b">
        <div className="flex flex-row w-full h-10 shrink-0 items-center justify-between p-4 gap-2">
          <div className="text-xs text-gray-500">Properties</div>
          <div className="flex flex-row gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <BookText
                  className={cn('h-3.5 w-3.5 cursor-pointer', {
                    'cursor-not-allowed  opacity-50': isEmpty(logbookRecord),
                  })}
                  onClick={() => {
                    if (isEmpty(logbookRecord)) {
                      toast({
                        description: (
                          <div className="flex flex-row gap-2 items-center">
                            <PDKToastInfoBadgeIcon className="size-4" />
                            <p>
                              No logbook record found with the CDK ID:{' '}
                              {deal.cdk_id}
                            </p>
                          </div>
                        ),
                      });

                      return;
                    }

                    const queryParams = getQueryParamsForScrollAndFocus({
                      uiType: 'logbook-record',
                      id: deal.cdk_id.toString(),
                    });
                    const url = urlWithQueryParams(
                      `/${hubPathIdentifier}/logbook`,
                      queryParams,
                    );
                    navigate({ to: url });
                  }}
                />
              </TooltipTrigger>
              <TooltipContent
                className="flex flex-row gap-2 items-center text-xs"
                side="bottom"
                align="end"
              >
                {isEmpty(logbookRecord) ? (
                  <p>No logbook record found</p>
                ) : (
                  <>Go to Logbook record </>
                )}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                  onClick={clipboardDealLink}
                />
              </TooltipTrigger>
              <TooltipContent
                className="flex flex-row gap-2 items-center text-xs"
                side="bottom"
                align="end"
              >
                Copy deal link{' '}
                <KeyboardKeyIcon>{HOTKEYS.DEAL_CLIPBOARD_LINK}</KeyboardKeyIcon>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <PDKCDKIcon
                  className={cn('cursor-pointer', {})}
                  onClick={() => clipboardCDKId(deal.cdk_id)}
                />
              </TooltipTrigger>
              <TooltipContent
                className="flex flex-row gap-2 items-center text-xs"
                side="bottom"
                align="end"
              >
                CDK ID{' '}
                <KeyboardKeyIcon>
                  {HOTKEYS.DEAL_CLIPBOARD_CDK_ID}
                </KeyboardKeyIcon>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <PDKBarcodeIcon
                  className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                  onClick={() =>
                    clipboardVIN(deal.vehicle_listing?.vehicle?.vin)
                  }
                />
              </TooltipTrigger>
              <TooltipContent
                className="flex flex-row gap-2 items-center text-xs"
                side="bottom"
                align="end"
              >
                VIN{' '}
                <KeyboardKeyIcon>{HOTKEYS.DEAL_CLIPBOARD_VIN}</KeyboardKeyIcon>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <PDKDBBarcodeIcon
                  className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                  onClick={() => clipboardDealId(deal.id)}
                />
              </TooltipTrigger>
              <TooltipContent
                className="flex flex-row gap-2 items-center text-xs"
                side="bottom"
                align="end"
              >
                VIN{' '}
                <KeyboardKeyIcon>{HOTKEYS.DEAL_CLIPBOARD_VIN}</KeyboardKeyIcon>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </header>

      <div
        className="flex flex-col p-2 gap-4 h-[calc(100vh-40px)] overflow-auto ml-1"
        ref={scrollContainerRef}
      >
        <div className="flex flex-col">
          <div className="text-xs text-gray-500 py-2 px-1">Date</div>
          <DealTimeStatusBadge
            status={deal.status}
            dateString={deal.created_at}
            className="w-fit"
          />
          <div className="flex flex-row gap-3 items-center">
            <Button variant="ghost" className="p-1 h-fit">
              <ClockFading className={cn('h-3.5 w-3.5 cursor-pointer', {})} />
              <p className="rounded text-xs  text-gray-600">
                {dayjs(deal.created_at).format('MMM D, YYYY h:mm A')}
              </p>
            </Button>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="text-xs text-gray-500 py-2 px-1">Risk</div>
          <div className="flex flex-row gap-3 items-center">
            {deal.risk == null ? (
              <Button variant="ghost" className="p-1 h-fit">
                <FileQuestion
                  className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                />
                <p className="rounded text-xs  text-gray-600">
                  No risk assessment
                </p>
              </Button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" className="p-1 h-fit">
                    {deal.risk.level == 'low' ? (
                      <>
                        <ShieldCheck
                          className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                        />
                        <p className="rounded text-xs  text-gray-600">
                          Low Risk
                        </p>
                      </>
                    ) : deal.risk.level == 'needs_attention' ? (
                      <>
                        <ShieldAlert
                          className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                        />
                        <p className="rounded text-xs  text-gray-600">
                          Attention Required
                        </p>
                      </>
                    ) : (
                      <>
                        <ShieldAlert
                          className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                        />
                        <p className="rounded text-xs  text-gray-600">
                          High Risk
                        </p>
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {deal.risk.level == 'low' ? (
                    <p>Risk of fraud is low, but never zero! :D</p>
                  ) : deal.risk.level == 'needs_attention' ? (
                    <>
                      <p>
                        Please ask a manager to review. Risk of fraud is not
                        high, but not low either.
                      </p>
                      <p>Checks failed</p>
                      <ul className="list-disc list-inside">
                        {deal.risk.checks_failed.map((check) => (
                          <li key={check}>{check}</li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <>
                      <p>Most likely fraud. Please ask a manager to review.</p>
                      <p>Checks failed</p>
                      <ul className="list-disc list-inside">
                        {deal.risk.checks_failed.map((check) => (
                          <li key={check}>{check}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="flex flex-row gap-3 items-center">
          <CDKIdEditor deal={deal} />
        </div>
        <div className="flex flex-row gap-3 items-center">
          <DealStatusSelector
            id={deal.id}
            shortId={dealShortId}
            value={deal.status}
            deal={deal}
            withHotkeyListener
            className={LEFT_PADDING}
          />
          {/* <DealStatusSelectorWithAI
            id={deal.id}
            shortId={dealShortId}
            value={deal.status}
            withHotkeyListener
            className={LEFT_PADDING}
            scrollContainerRef={scrollContainerRef}
          /> */}
        </div>
        <div className="flex flex-row gap-3 items-center">
          <DealStageSelector
            id={deal.id}
            shortId={dealShortId}
            value={deal.stage}
            withHotkeyListener
            className={LEFT_PADDING}
          />
          {/* <DealStageSelectorWithAI
            id={deal.id}
            shortId={dealShortId}
            value={deal.stage}
            withHotkeyListener
            className={LEFT_PADDING}
            scrollContainerRef={scrollContainerRef}
          /> */}
        </div>
        <div className="flex flex-row gap-3 items-center">
          <DealDeliveryPreferenceSelector
            dealId={deal.id}
            delivery_preference={deal.delivery_preference}
            withHotkeyListener
            className={LEFT_PADDING}
          />
        </div>

        {deal.vehicle_listing && (
          <div className="flex flex-col">
            <div className="text-xs text-gray-500 p-2">Delivery Queued</div>
            <DeliveryQueueSelector
              dealId={deal.id}
              // vehicle={deal.vehicle_listing}
              pickupDate={deal.pickup_date}
              delivery_date={deal.delivery_date}
              date_added={deal.date_added_to_customer_delivery_queue}
              className={LEFT_PADDING}
            />
          </div>
        )}

        <div className="flex flex-col">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-xs text-gray-500 p-2">Pickup Date</div>
            </TooltipTrigger>
            <TooltipContent
              align="start"
              className="flex flex-row gap-1 items-center"
            >
              Change Pickup Date{' '}
              <KeyboardKeyIcon>{HOTKEYS.DEAL_PICKUP_DATE}</KeyboardKeyIcon>
            </TooltipContent>
          </Tooltip>
          <PickupDateSelector
            pickup_date={deal.pickup_date}
            dealId={deal.id}
            withHotkeyListener
            withInput
            className={LEFT_PADDING}
          />
        </div>

        <div className="flex flex-col">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-xs text-gray-500 p-2">Delivery Date</div>
            </TooltipTrigger>
            <TooltipContent
              align="start"
              className="flex flex-row gap-1 items-center"
            >
              Change Delivery Date{' '}
              <KeyboardKeyIcon>{HOTKEYS.DEAL_DELIVERY_DATE}</KeyboardKeyIcon>
            </TooltipContent>
          </Tooltip>
          <DeliveryDateSelector
            delivery_date={deal.delivery_date}
            dealId={deal.id}
            withHotkeyListener
            withInput
            className={LEFT_PADDING}
          />
        </div>

        <div className="flex flex-col">
          <div className="text-xs text-gray-500 px-2 py-1">Lender</div>
          <DealLenderSelector withHotkeyListener className="px-1" />
        </div>

        <div className="flex flex-col gap-0">
          <div className="flex flex-row gap-1 items-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-row gap-1 items-center  text-xs text-gray-500 p-2">
                  Add Trade
                  <div className="flex flex-row gap-3 items-center">
                    <TradeCreateButton dealId={deal.id} />
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Please use this over the Simple Trade-In YMM field</p>
              </TooltipContent>
            </Tooltip>
          </div>

          {deal.appraisals.map((appraisal) => (
            <Button
              key={appraisal.id}
              onClick={() => {
                window.open(getAppraisalPathname(appraisal.id), '_blank');
              }}
              variant="ghost"
              className="w-fit h-fit px-2 py-1 text-xs text-gray-500 "
            >
              {appraisal.year} {appraisal.make} {appraisal.model}
              <PDKOpenInNewTabIcon className="size-3.5" />
            </Button>
          ))}
        </div>
        <div className="flex flex-col">
          <div className="text-xs text-gray-500 p-2">Finance Manager</div>
          <div className="flex flex-row gap-3 items-center">
            <FinanceManagerSelector
              id={deal.id}
              value={deal.finance_manager}
              withHotkeyListener
              className="px-1"
            />
          </div>
        </div>

        <div className="flex flex-col">
          <div className="text-xs text-gray-500 p-2">Sales Reps</div>
          {deal.vehicle_listing && deal.primary_customer?.full_name && (
            <SalesRepSelectors
              dealShortId={dealShortId}
              dealId={deal.id}
              shouldRedirectToInformativeDealUrl
              withHotkeyListener
              primarySalesRep={deal.primary_sales_rep}
              secondarySalesRep={deal.secondary_sales_rep}
              vehicle={deal.vehicle_listing}
              primaryCustomerFullName={deal.primary_customer?.full_name}
              className="px-1"
            />
          )}
        </div>

        <div className="flex flex-col">
          <div className="text-xs text-gray-500 p-2">Files</div>
          <div className="flex flex-row gap-3 items-center">
            <Files deal={deal} className="px-1" />
          </div>
        </div>
      </div>
      {isAISuggestionsEnabled && (
        <FloatingAIBulkSuggestions
          dealId={deal.id}
          suggestions={suggestionsForDeal}
          taskSuggestions={taskSuggestionsForDeal}
        />
      )}
    </div>
  );
});

const ContextMenuWrapper = (Component: typeof NonMobileAside) => {
  return ({ deal }: { deal: Deal }) => {
    const dealShortId = deal.short_id ?? '';
    const { toggleFavorite } = useFavoriteMutation(deal);
    const [openDialog, setOpenDialog] = useState<HotkeyDialogBox | null>(null);
    const [tradeIn, setTradeIn] = useState<Deal['trades'][0] | null>(null);
    const [createLenderDialogIsOpen, setCreateLenderDialogIsOpen] =
      useState(false);

    const { dealActions, handleCreateLender } = useDealActions({
      deal,
      dealShortId,
      setOpenDialog,
      setTradeIn,
      setCreateLenderDialogIsOpen,
    });

    return (
      <ContextMenu>
        {tradeIn && (
          <TradeInEditDialog
            dealId={deal.id}
            tradeIn={tradeIn}
            dialogIsOpen={!!tradeIn}
            setDialogIsOpen={(open) => {
              if (!open) {
                setTradeIn(null);
              }
            }}
            onSuccess={() => {
              toast({
                title: `Trade-in successfully updated`,
              });
            }}
            onError={(error) => {
              toast({
                title: `Error updating trade-in`,
                description: error.message,
              });
            }}
          ></TradeInEditDialog>
        )}
        {handleCreateLender && (
          <LenderDialog
            initialName={''}
            initialEmail={''}
            initialPhone={''}
            initialLenderType={'bank'}
            onClickCta={handleCreateLender}
            ctaText={'Create Lender'}
            onOpen={() => setCreateLenderDialogIsOpen(true)}
            onClose={() => setCreateLenderDialogIsOpen(false)}
            dialogIsOpen={createLenderDialogIsOpen}
          ></LenderDialog>
        )}
        <HotkeyDialogBoxes
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
          selectedDeal={deal}
        />
        <ContextMenuTrigger>
          <Component deal={deal} />
        </ContextMenuTrigger>
        <ContextMenuContent className="w-52">
          <ContextMenuItem onClick={toggleFavorite} className="text-xs">
            <Star
              className={cn('h-3.5 w-3.5 cursor-pointer', {
                'fill-yellow-500': deal.favorited,
              })}
            />
            {deal.favorited ? 'Unfavorite' : 'Favorite'}
          </ContextMenuItem>
          <ContextMenuSub>
            <ContextMenuSubTrigger className="gap-2 text-xs">
              <Copy className="size-3.5" />
              Copy
            </ContextMenuSubTrigger>
            <ContextMenuSubContent className="w-60 text-xs">
              <ContextMenuItem onClick={clipboardDealLink} className="text-xs">
                <Link className="size-3.5" />
                Copy URL
                {/* command shift l */}
                <ContextMenuShortcut>
                  <KeyboardKeyIcon>
                    {HOTKEYS.DEAL_CLIPBOARD_LINK}
                  </KeyboardKeyIcon>
                </ContextMenuShortcut>
              </ContextMenuItem>
              <ContextMenuItem
                onClick={() => clipboardCDKId(deal.cdk_id)}
                className="text-xs"
                disabled={!deal.cdk_id}
              >
                <PDKCDKIcon className="border-none p-0" />
                {deal.cdk_id ? 'Copy CDK ID' : 'No CDK ID'}
                {/* command shift C */}
                <ContextMenuShortcut>
                  <KeyboardKeyIcon>
                    {HOTKEYS.DEAL_CLIPBOARD_CDK_ID}
                  </KeyboardKeyIcon>
                </ContextMenuShortcut>
              </ContextMenuItem>
              <ContextMenuItem
                onClick={() => clipboardVIN(deal.vehicle_listing?.vehicle?.vin)}
                className="text-xs"
              >
                <PDKBarcodeIcon className="size-3.5" />
                Copy VIN
                {/* command shift I */}
                <ContextMenuShortcut>
                  <KeyboardKeyIcon>
                    {HOTKEYS.DEAL_CLIPBOARD_VIN}
                  </KeyboardKeyIcon>
                </ContextMenuShortcut>
              </ContextMenuItem>
            </ContextMenuSubContent>
          </ContextMenuSub>
          {dealActions.map((action) => (
            <ContextMenuItem
              key={action.value}
              onClick={action.onClick}
              className="text-xs"
            >
              {action.icon}
              {action.label}
            </ContextMenuItem>
          ))}
        </ContextMenuContent>
      </ContextMenu>
    );
  };
};

export default ContextMenuWrapper(NonMobileAside);
